import { EventObject, Typestate } from 'xstate';
import {
  AcknowledgeLeaseTermsError,
  CartItemValidation,
  CartValidation,
  CustomerEligibility,
  GenerateContractErrors,
  GetExistingLeaseDocumentsErrors,
  LeaseDocuments,
  LeaseItem,
  ProtectionPlusEnrollmentError,
  RemoveVirtualCardErrors,
  SelectLeaseOptionErrors,
  SigningErrors,
  UpdateOrderErrors,
} from '../../gql/graphql';
import { EmbedUrlError } from '../../networkRequests/virtualCard/getEmbedURL';
import { SupportedLanguage } from '../../helpers/language/language.helper';
import { CartItemModel, EditCartItemModel } from './existingApplicationMachine.types';
import { ApplyRequestMachineContext, ApplyResponseMachineContext } from './shared/context/applyMachineContext';
import { OrderIdMachineContext } from './shared/context/orderIdMachineContext';
import { OrderMachineContext } from './shared/context/orderMachineContext';
import { ValidatedByTokenType } from './ecommMachine.types';
import { RoleMachineContext } from './shared/context/roleMachineContext';

export const POST_DECISION_MACHINE_ID = 'PostDecisionMachine';

// Context Types
export interface PostDecisionMachineContext
  extends RoleMachineContext,
    OrderIdMachineContext,
    OrderMachineContext,
    ApplyRequestMachineContext, // provides billingAddress for the debit card update
    ApplyResponseMachineContext {
  // forwarded from other machines
  highestSupportedApplicationMilestone: number;
  // Which link the customer arrived on. OOBA is the emailed M4 brick and mortar signing link, and
  // RouteDocsReady reads it - that session has no contract summary access yet must reach signing.
  validatedByTokenType?: ValidatedByTokenType;

  canGoBackViaHeader: boolean;
  // The page to open instead of deriving one from status. Set from the parent's
  // postDecisionStartingPage. Entry honours CartSummary, ContractSummaryEcomm, ContractSummaryBM and
  // ApprovalConfirmation and ignores every other value. If not set, uses status checks
  skipToPage?: string | null;
  contractSummaryPageParam: boolean;
  merchantContractSummaryPageParam: boolean;
  // resolved by EntryPointV2; used to derive the B&M contract summary entry before applicationItems is fetched
  hasAppItems?: boolean;
  // from the customer search that ran before the app was decided; the approval pages read the otb
  // lease options off it. Comes along in the parent's context spread.
  eligibilityDetails?: CustomerEligibility;

  selectedLeaseTerm?: string; // comes from the selectLeaseOption resp. This is NOT defaulted to the selectedLeaseTerm application property
  customerSelectedLeaseTerm?: string;
  selectLeaseOptionErrors?: SelectLeaseOptionErrors[];
  acknowledgeLeaseTermsErrors?: AcknowledgeLeaseTermsError[];

  leaseDocuments?: LeaseDocuments;
  // OR-2886: the language `leaseDocuments` was rendered in, and a per-language cache so toggling
  // back does not regenerate. See actions/leaseDocumentLanguage.ts.
  leaseDocumentsLanguage?: SupportedLanguage;
  requestedLeaseDocumentsLanguage?: SupportedLanguage;
  leaseDocumentsByLanguage?: Partial<Record<SupportedLanguage, LeaseDocuments>>;
  /** Read-only only: whether the persisted unsigned English companion is on file for this lease. */
  hasEnglishLeaseCompanion?: boolean;
  generateContractErrors: GenerateContractErrors[];
  getExistingLeaseDocumentsErrors: GetExistingLeaseDocumentsErrors[];
  signingErrors: SigningErrors[];
  ipAddress: string;
  leaseSigned: boolean;

  cardExpiration: number;
  embedUrl: string;
  embedUrlError: EmbedUrlError | null;
  removeVirtualCardErrors: RemoveVirtualCardErrors[];

  cart: CartItemModel[];
  cartTotal: number; // represents the user inputted cart total
  itemToEdit: EditCartItemModel | null;

  updateOrderErrors?: UpdateOrderErrors[];
  cartValidationErrors?: CartValidation[]; // cart level errors
  cartValidationItemErrors?: Record<string, CartItemValidation>; // item level warnings
  cartValidationItemWarnings?: Record<string, CartItemValidation>; // item and cart level warnings?
  autoremovedCartItemNames?: string[];

  fromContractSummary: boolean;

  applicationItems: LeaseItem[]; // make sure this is populated before you use it

  showNotAcknowledgedModal?: boolean;
  protectionPlusEnrollmentErrors?: ProtectionPlusEnrollmentError[];
}

// State Types
export enum PostDecisionStates {
  Entry = 'Entry',
  // Transient routing states. CheckLeaseStatus dispatches on lease status alone; each RouteX below
  // then picks the page from the dealer and session shape. None of them render.
  RouteApproved = 'RouteApproved',
  RouteDocsReady = 'RouteDocsReady',
  RouteFinished = 'RouteFinished',
  // The cart validation requery an approved virtual card session makes before RouteApproved
  VirtualCardCartCheck = 'VirtualCardCartCheck',

  Approval = 'Approval',
  ItemEntry = 'ItemEntry',
  CartSummary = 'CartSummary',
  CartTotal = 'CartTotal',
  CartReviewLoading = 'CartReviewLoading',
  CartError = 'CartError',

  ContractSummaryEcomm = 'ContractSummaryEcomm',
  ContractSummaryBM = 'ContractSummaryBM',
  DeferContractSigning = 'DeferContractSigning',
  LeaseAgreement = 'LeaseAgreement',
  ApprovalConfirmation = 'ApprovalConfirmation',

  VirtualCardPage = 'VirtualCardPage',

  InternalError = 'InternalError',

  // terminal states
  LeaseSigned = 'LeaseSigned',
  MerchantRedirect = 'MerchantRedirect',
  ExitToDebitCardEntry = 'ExitToDebitCardEntry',
  ProtectionPlusRedirect = 'ProtectionPlusRedirect',
}

export interface PostDecisionMachineStatesSchema {
  [PostDecisionStates.Entry]: {};
  [PostDecisionStates.RouteApproved]: {};
  [PostDecisionStates.RouteDocsReady]: {};
  [PostDecisionStates.RouteFinished]: {};
  [PostDecisionStates.VirtualCardCartCheck]: {};
  [PostDecisionStates.Approval]: {};
  [PostDecisionStates.ItemEntry]: {};
  [PostDecisionStates.CartSummary]: {};
  [PostDecisionStates.CartTotal]: {};
  [PostDecisionStates.CartReviewLoading]: {};
  [PostDecisionStates.CartError]: {};
  [PostDecisionStates.ContractSummaryEcomm]: {};
  [PostDecisionStates.ContractSummaryBM]: {};
  [PostDecisionStates.DeferContractSigning]: {};
  [PostDecisionStates.LeaseAgreement]: {};
  [PostDecisionStates.ApprovalConfirmation]: {};
  [PostDecisionStates.VirtualCardPage]: {};
  [PostDecisionStates.InternalError]: {};
  [PostDecisionStates.LeaseSigned]: {};
  [PostDecisionStates.MerchantRedirect]: {};
  [PostDecisionStates.ExitToDebitCardEntry]: {};
  [PostDecisionStates.ProtectionPlusRedirect]: {};
}

export interface PostDecisionMachineStates extends Typestate<PostDecisionMachineContext> {
  type: PostDecisionMachineStatesSchema;
}

// Event Types
export enum PostDecisionEvents {
  NEXT = 'NEXT',
  BACK = 'BACK',
  RETRY = 'RETRY',
  ERROR = 'ERROR',
  EDIT_DEBIT_INFO = 'EditDebitInfo',
  REMOVE_VIRTUAL_CARD = 'RemoveVirtualCard',
  EDIT_CART_ITEM = 'EditCartItem',
  EDIT_CART = 'EditCart',
  DELETE_CART_ITEM = 'DeleteCartItem',
  TRACK = 'TRACK',
  PAGE = 'PAGE',
  IDENTIFY = 'IDENTIFY',
  // ContractSummaryBM OOBA Resend events
  REFRESH_CONTRACT_SUMMARY_STATUS = 'RefreshContractSummaryStatus',
  CLOSE_NOT_ACKNOWLEDGED_MODAL = 'CloseNotAcknowledgedModal',
  // LeaseAgreement SignalR events
  REFRESH_SIGNING_STATUS = 'RefreshSigningStatus',
  // LeaseAgreement footer language toggle
  LANGUAGE_CHANGED = 'LanguageChanged',
}

type PostDecisionEventsTypeSchema =
  | PostDecisionEvents.NEXT
  | PostDecisionEvents.BACK
  | PostDecisionEvents.RETRY
  | PostDecisionEvents.ERROR
  | PostDecisionEvents.EDIT_DEBIT_INFO
  | PostDecisionEvents.REMOVE_VIRTUAL_CARD
  | PostDecisionEvents.EDIT_CART_ITEM
  | PostDecisionEvents.EDIT_CART
  | PostDecisionEvents.DELETE_CART_ITEM
  | PostDecisionEvents.TRACK
  | PostDecisionEvents.PAGE
  | PostDecisionEvents.IDENTIFY
  | PostDecisionEvents.REFRESH_CONTRACT_SUMMARY_STATUS
  | PostDecisionEvents.CLOSE_NOT_ACKNOWLEDGED_MODAL
  | PostDecisionEvents.REFRESH_SIGNING_STATUS
  | PostDecisionEvents.LANGUAGE_CHANGED;

export interface PostDecisionMachineEvents extends EventObject {
  type: PostDecisionEventsTypeSchema;
  data?: any;
}

// Guard Types
export enum PostDecisionGuardsType {
  VIRTUAL_CARD_DEALER = 'VirtualCardDealer',
  IS_SHORT_FORM = 'IsShortForm',
}
type PostDecisionGuardsTypeSchema = PostDecisionGuardsType.VIRTUAL_CARD_DEALER | PostDecisionGuardsType.IS_SHORT_FORM;

export interface PostDecisionMachineGuards extends EventObject {
  type: PostDecisionGuardsTypeSchema;
}
